import { useEffect, useState, Suspense } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

// Common components
import Footer from "../../components/Footer";
import Sidebar from "../../components/common/Sidebar";
import Header from "../../components/common/Header";
import BreadCrumb from "../../components/BreadCrumb";
import CardWrapper from "../../components/board/CardWrapper";
import FeteDescription from "../../components/FeteDescription";
import FeteEditModal from "../../components/FeteEditModal";
import FeteSettingModal from "../../components/FeteSettingModal";
import InviteCollaborator from "../../components/InviteCollaborator";

import useAppState from "../../context/useAppState";

// Icons
import starIcon from "../../assets/images/star-icon.svg";
import editIcon from "../../assets/images/edit-icon.svg";
import shareIcon from "../../assets/images/share-icon.svg";
import settingIcon from "../../assets/images/settings-icon.svg";
import crossIcon from "../../assets/images/cross-icon.svg";
import { PROMPT_DEFAULTS, ROLES } from "utils/constant";
import ChatBox from "components/ChatBox";
import AssignTaskModal from "components/common/modal/AssignTaskModal";
import FeteDueDateModal from "components/FeteDueDateModal";
import FeteAddRemainder from "components/FeteAddRemainder";
import UploadFileModal from "components/chat/Body/UploadDocument";
import { CHAT_SESSION, DEFAULT_FETEBUDDY_INITIATE_MESSAGES, DEFAULT_FETEBUDDY_PROMPT_KEYS, MESSAGE_TYPES } from "constants/llm";
import { isNewFete } from "utils/fete";
import { getAnswerFromResponse } from "utils/llm";
import { getTimeForChatMessage, jsonToMarkdown } from "utils/function";
import { FeteDescriptionLoader } from "components/loaders";

const PlanningBoard = () => {
	const { t } = useTranslation();
	const location = useLocation();
	const navigate = useNavigate();
	const { id } = useParams();
	const [openInviteCollaborators, setOpenInviteCollaborators] = useState(false);
	const [isChatInitiated, setIsChatInitiated] = useState(false)

	const {
		eventFetchedData,
		fetchEventData,
		updateEventCallApiFlag,
		isEventCallApi,
		openPlanningAnimation,
		showChatbox,
		setState,
		feteDescriptionLoader,
		showInsight,
		eventTasks
	} = useAppState("eventData");

	const {
		cardAnimation,
		setState: setCardWrapperState
	} = useAppState('cardWrapper');

	const { selectedCardDetail } = useAppState('cardWrapper');
	const { draftEventTasks } = useAppState('newFeteChat');
	const { setState: setChatMessagesState, createSession, getUserChatSessions } = useAppState('chatMessages');
	const { setState: setChatboxState, getLLMResponse, currentSession } = useAppState("chatBox");

	const userData = JSON.parse(localStorage.getItem('userData') as string)
	const currentTeam = JSON.parse(localStorage.getItem('selectedTeam') as string)

	const getMetadataFilter = () => {
		let metadata_filter: any = {
			teamId: currentTeam?.currentTeamId,
			userId: userData?.id
		}
		if (isNewFete()) {
			return metadata_filter;
		}
		if (id) {
			metadata_filter['eventId'] = id
		}
		if (selectedCardDetail) {
			metadata_filter['cardId'] = selectedCardDetail?.id
		}
		return metadata_filter;
	}

	const getMetadataFilterForLLM = () => {
		let metadata_filter: any = {
			teamId: currentTeam?.currentTeamId,
		}
		if (isNewFete()) {
			return metadata_filter;
		}
		if (id) {
			metadata_filter['eventId'] = id
		}
		return metadata_filter;
	}

	const getChatSessions = async () => {
		const userSessions = await getUserChatSessions({
			page: 0,
			perPage: 10,
			filter: getMetadataFilter(),
		});
		return userSessions;
	}

	useEffect(() => {
		if (eventFetchedData?.is_draft) {
			navigate(`/fete/new/${eventFetchedData?._id}`)
		}
	}, [eventFetchedData])

	const getTasksInput = () => {
		if (selectedCardDetail) {
			return jsonToMarkdown(selectedCardDetail?.tasks)
		}
		if (id) {
			return jsonToMarkdown(eventTasks)
		}
		if (draftEventTasks && draftEventTasks?.length > 0) {
			return jsonToMarkdown(draftEventTasks)
		}
		return null;
	}

	const getLLMInputVariables = () => {
		const vars: any = {
			is_vendor: true,
			json_formats: PROMPT_DEFAULTS.JSON_FORMATS,
			notes: PROMPT_DEFAULTS.NOTES
		};
		if (eventFetchedData) {
			vars['event_details'] = jsonToMarkdown(eventFetchedData)
		}
		if (getTasksInput() !== '') {
			vars['task_list'] = getTasksInput()
		}
		return vars;
	}

	useEffect(() => {
		if (isChatInitiated) {
			(async () => {
				setChatMessagesState({ messageHistory: [], showChatLoader: true })
				const sessionRes = await createSession({
					title: CHAT_SESSION.DRAFT_TITLE,
					metadata: getMetadataFilter()
				});
				const userSessions = await getChatSessions();
				setChatboxState({ currentSession: sessionRes, userChatSessions: userSessions?.sessions || [] })
				const llmRes = await getLLMResponse({
					input_variables: getLLMInputVariables(),
					userMessage: `${DEFAULT_FETEBUDDY_INITIATE_MESSAGES.CONTINUE}`,
					promptKey: DEFAULT_FETEBUDDY_PROMPT_KEYS.CONTINUE,
					session_id: sessionRes?._id,
					metadata_filter: getMetadataFilterForLLM()
				});
				setChatMessagesState({ messageHistory: [{ type: MESSAGE_TYPES.AI, content: getAnswerFromResponse(llmRes), createdAt: new Date().toISOString() }], showChatLoader: false })
				setIsChatInitiated(false)
			})()
		}
	}, [isChatInitiated])

	const [isShowSettingModal, setIsShowSettingModal] = useState(false);

	const [openEditModal, setOpenEditModal] = useState(false);

	const Shimmer: React.FC = () => {
		return (
			<div className="h-10 w-[42px] flex gap-x-3">
				<div className="rounded-md w-[42px] bg-gray-100"></div>
			</div>
		);
	};

	useEffect(() => {
		if (isEventCallApi) {
			fetchEventData(id);
		}
	}, [isEventCallApi]);

	return (
		<div className="dashboard-wrapper flex">
			{/* Sidebar */}
			<Sidebar />

			{/* dashboard main */}
			<div className="w-[calc(100%_-_72px)] ml-auto">
				{/* Header */}
				<Header />

				{/* Main */}
				{/* planning-animation */}
				<div
					className={`pt-6 h-[calc(100vh_-_57px)] scroll-to-top overflow-y-scroll ${openPlanningAnimation == true ? "planning-animation smoothness" : ""
						} ${cardAnimation == true ? "card-animation smoothness" : ""}`}
				>
					{/* Controls */}

					<div className="card-anim-structure-upper overflow-hidden px-6">
						<div className="card-anim-structure-lower">
							<div className="flex controls-buttons-wrapper transform transition duration-0">

								{/* Product launch */}
								<FeteDescriptionLoader count={feteDescriptionLoader ? 1 : 0} />
								{!feteDescriptionLoader && <FeteDescription
									eventData={eventFetchedData}
									setOpenInviteCollaborators={setOpenInviteCollaborators}
								/>}

								<div className="flex items-center gap-x-3 self-start ml-auto controls-buttons flex-shrink-0">
									{/* Fete Buddy Button */}
									<button
										onClick={() => {
											setState({ openPlanningAnimation: true, showChatbox: true, showInsight: false, showReviewSpace: false });
											setCardWrapperState({ cardAnimation: false });
											setIsChatInitiated(true)
										}}
										className="py-2.5 px-3.5 rounded-lg bg-gradient-1 font-interSemiBold text-sm text-white gap-x-1 flex items-center hover-fete-effect"
									>
										<img src={starIcon} alt="star" />
										Fete Buddy
									</button>

									{
										eventFetchedData?.eventRole?.name === ROLES.OWNER &&
										<Suspense fallback={<Shimmer />}>
											<button
												onClick={() => {
													setOpenEditModal(true);
												}}
												className="px-2.5 py-2.5 border border-[#D0D5DD] rounded-lg hover:bg-[#f2f4f7]"
											>
												<img src={editIcon} alt="edit icon" />
											</button>
										</Suspense>
									}

									{
										eventFetchedData?.eventRole?.name === ROLES.OWNER &&
										<Suspense fallback={<Shimmer />}>
											<button
												className="px-2.5 py-2.5 border border-[#D0D5DD] rounded-lg hover:bg-[#f2f4f7]"
												onClick={() => setIsShowSettingModal(true)}
											>
												<img src={settingIcon} alt="setting icon" />
											</button>
										</Suspense>
									}
								</div>
							</div>

							{/* Cards Section */}
							<CardWrapper />

							{/* Modal Should be here */}
							<InviteCollaborator
								setOpenInviteCollaborators={setOpenInviteCollaborators}
								openInviteCollaborators={openInviteCollaborators}
							/>

							<AssignTaskModal />
							<FeteDueDateModal />
							<FeteAddRemainder />
							<UploadFileModal />

						</div>
						{/* id="planning-board-scroll-top" */}
						<div id="planning-board-scroll-top" className="card-fete-chatbox rounded-bl-2xl rounded-br-2xl">
							{showChatbox && (
								<ChatBox />
							)}
						</div>
					</div>

					{/* Footer */}
					<Footer />

					{/* Setting Modal*/}
					{
						// isShowSettingModal &&
						<FeteSettingModal
							isShowSettingModal={isShowSettingModal}
							setIsShowSettingModal={setIsShowSettingModal}
						/>
					}


					{/* EditModal */}
					{
						openEditModal &&
						<FeteEditModal
							isShow={openEditModal}
							setOpenEditModal={setOpenEditModal}
						/>
					}
				</div>
			</div>
		</div>
	);
};

export default PlanningBoard;
